<template>
  <div class="newsflash-page">
    <div class="informe-main-container">
      <div class="informe-header">
        <h1 class="typo-headline-4">{{ headerTitle }}</h1>
        <p class="typo-body-2">{{ headerDescription }}</p>
      </div>

      <div class="newsflash-content-single">
        <main class="main-column main-column-full">
          <section v-if="pageLoading" class="content-state">
            <div class="loading-spinner loading-spinner-large"></div>
            <h2>Cargando noticias flash</h2>
            <p>Estamos preparando el contenido.</p>
          </section>

          <section v-else-if="blockingError" class="content-state">
            <span class="material-icons-outlined state-icon">error</span>
            <h2>No se pudo cargar el contenido</h2>
            <p>{{ blockingError }}</p>
            <button type="button" class="read-more-button" @click="reloadCurrentView">
              Reintentar
            </button>
          </section>

          <section v-else-if="isDetailView && currentItem" class="newsflash-detail">
            <article class="detail-card">
              <div class="detail-topbar">
                <div class="detail-topline">
                  <span class="meta-chip">{{ formatPublicationDate(currentItem.publication_date) }}</span>
                </div>

                <button
                  type="button"
                  class="read-more-button"
                  @click="goToList"
                >
                  Volver al listado
                </button>
              </div>

              <div class="detail-block">
                <h2 class="detail-title">Que ha pasado</h2>
                <p class="detail-text">{{ currentItem.what_happened }}</p>
              </div>

              <div class="detail-block">
                <h2 class="detail-title">Consecuencias economicas</h2>
                <p class="detail-text">{{ currentItem.economic_consequences }}</p>
              </div>

              <div class="detail-block">
                <div class="detail-block-header">
                  <h2 class="detail-title">Valores relacionados</h2>
                </div>

                <div v-if="currentItem.affected_stocks_count === 0" class="empty-placeholder">
                  No hay valores relacionados registrados para esta noticia.
                </div>

                <div v-else class="stocks-list">
                  <article
                    v-for="stock in currentItem.affected_stocks"
                    :key="`${currentItem.slug}-${stock.ticker}`"
                    class="stock-item"
                  >
                    <div class="stock-item-header">
                      <span class="stock-ticker">{{ stock.ticker }}</span>
                      <span class="stock-name">{{ stock.nombre_empresa }}</span>
                    </div>

                    <div v-if="stock.a_que_se_dedica" class="stock-detail-group">
                      <p class="stock-detail-label">A qué se dedica</p>
                      <p class="stock-reason">{{ stock.a_que_se_dedica }}</p>
                    </div>

                    <div class="stock-detail-group">
                      <p class="stock-detail-label">Cómo se ve afectado</p>
                      <p class="stock-reason">{{ stock.como_se_ve_afectado }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </article>
          </section>

          <section v-else class="newsflash-list">
            <div v-if="!items.length" class="content-state">
              <span class="material-icons-outlined state-icon">newspaper</span>
              <h2>No hay noticias flash publicadas</h2>
              <p>Cuando se aprueben nuevas noticias aparecerán aquí.</p>
            </div>

            <template v-else>
              <article
                v-for="item in items"
                :key="item.id"
                class="informe-item newsflash-item"
              >
                <router-link :to="buildItemPath(item.slug)" class="newsflash-item-shell newsflash-item-link">
                  <div class="newsflash-item-header">
                    <span class="meta-chip">{{ formatPublicationDate(item.publication_date) }}</span>
                  </div>

                  <h2 class="informe-title newsflash-title-link">
                    {{ item.title }}
                  </h2>

                  <div class="newsflash-card-footer">
                    <span class="newsflash-cta">
                      Leer noticia
                      <span class="material-icons-outlined">arrow_forward</span>
                    </span>
                  </div>
                </router-link>
              </article>
            </template>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import APICaller from '@/components/codeComponents/APICaller.js';

export default {
  name: 'NoticiasFlash',
  mixins: [APICaller],
  data() {
    return {
      items: [],
      currentItem: null,
      listLoading: false,
      detailLoading: false,
      listError: '',
      detailError: '',
    };
  },
  computed: {
    isDetailView() {
      return Boolean(this.$route.params.slug);
    },
    pageLoading() {
      if (this.isDetailView) {
        return this.detailLoading && !this.currentItem;
      }

      return this.listLoading && !this.items.length;
    },
    blockingError() {
      if (this.isDetailView) {
        return this.currentItem ? '' : this.detailError;
      }

      return this.items.length ? '' : this.listError;
    },
    headerTitle() {
      if (this.isDetailView && this.currentItem) {
        return this.currentItem.title;
      }

      return 'Noticias flash';
    },
    headerDescription() {
      if (this.isDetailView && this.currentItem) {
        return 'Detalle de la noticia flash publicada, con su impacto y los valores afectados.';
      }

      return 'Accede a las noticias flash publicadas y validadas por el equipo.';
    },
  },
  watch: {
    '$route.params.slug'() {
      this.loadViewData();
    },
  },
  mounted() {
    this.loadViewData();
  },
  methods: {
    loadViewData() {
      window.scrollTo(0, 0);
      this.fetchList();

      if (this.isDetailView) {
        this.fetchItem();
      }
      else {
        this.currentItem = null;
        this.detailError = '';
        this.detailLoading = false;
      }
    },
    fetchList() {
      this.listLoading = true;
      this.listError = '';

      const successHandler = new APICaller.SuccessHandler(response => {
        this.items = Array.isArray(response.data) ? response.data : [];
        this.listLoading = false;
      });

      const failureHandler = new APICaller.FailureHandler('400', 'Error al cargar las noticias flash', error => {
        this.listError = this.resolveErrorMessage(error, 'No se pudo cargar el listado de noticias flash.');
        this.listLoading = false;
      });

      this._getAPICall('/api/newsflash-ai/published', successHandler, failureHandler);
    },
    fetchItem() {
      const slug = this.$route.params.slug;

      this.detailLoading = true;
      this.detailError = '';
      this.currentItem = null;

      const successHandler = new APICaller.SuccessHandler(response => {
        this.currentItem = response.data || null;
        this.detailLoading = false;
      });

      const failureHandler = new APICaller.FailureHandler('404', 'Error al cargar la noticia flash', error => {
        this.detailError = this.resolveErrorMessage(error, 'La noticia flash solicitada no existe o ya no está disponible.');
        this.detailLoading = false;
      });

      this._getAPICall(`/api/newsflash-ai/published/${encodeURIComponent(slug)}`, successHandler, failureHandler);
    },
    reloadCurrentView() {
      this.loadViewData();
    },
    goToList() {
      this.$router.push('/app/noticias-flash');
    },
    buildItemPath(slug) {
      return `/app/noticias-flash/${encodeURIComponent(slug)}`;
    },
    formatPublicationDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
    resolveErrorMessage(error, fallback) {
      const response = error && error.response ? error.response : null;

      if (response && response.status === 404) {
        return 'La noticia flash solicitada no existe.';
      }

      if (response && response.data && typeof response.data.message === 'string') {
        return response.data.message;
      }

      return fallback;
    },
  },
  metaInfo() {
    const detailTitle = this.currentItem && this.currentItem.title
      ? `${this.currentItem.title} | Noticias flash`
      : 'Noticias flash | Eurekers';

    return {
      title: detailTitle,
    };
  },
};
</script>

<style lang="scss" scoped>
.newsflash-page {
  min-height: 100%;
}

.informe-main-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.informe-header {
  margin-bottom: 40px;
  text-align: center;

  h1 {
    color: var(--color-primary);
    margin-bottom: 12px;
    font-weight: 600;
  }

  p {
    color: var(--color-text-secondary);
    max-width: 640px;
    margin: 0 auto;
  }
}

.newsflash-content-single {
  margin-bottom: 40px;
}

.main-column {
  width: 100%;
}

.main-column-full {
  max-width: 920px;
  margin: 0 auto;
}

.empty-placeholder,
.detail-text,
.stock-reason,
.content-state p {
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.content-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.content-state {
  min-height: 320px;
  padding: 32px;
  border-radius: 16px;
  background-color: var(--color-surface);
  text-align: center;
  box-shadow: 0 8px 16px rgba(15, 15, 26, 0.05);

  h2 {
    margin: 0;
    color: var(--color-primary);
  }
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--color-outline);
  border-top-color: var(--color-highlight);
  animation: spin 1s linear infinite;
}

.loading-spinner-large {
  width: 40px;
  height: 40px;
  border-width: 3px;
}

.state-icon {
  font-size: 34px;
  color: var(--color-error);
}

.newsflash-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.newsflash-item {
  background: linear-gradient(135deg, var(--color-surface) 0%, var(--color-background) 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(15, 15, 26, 0.05);
  border: 1px solid var(--color-outline);
  transition: transform 0.3s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-highlight);
    box-shadow: 0 14px 26px rgba(15, 15, 26, 0.08);
  }
}

.newsflash-item-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px;
}

.newsflash-item-link {
  color: inherit;
  text-decoration: none;
}

.detail-topline,
.detail-block-header,
.stock-item-header,
.newsflash-item-header {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.meta-chip,
.ticker-chip,
.stock-ticker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.meta-chip,
.ticker-chip,
.stock-ticker {
  background-color: var(--color-active-surface);
  color: var(--color-text-primary);
}

.item-date {
  color: var(--color-text-secondary);
  font-size: 14px;
}

.informe-title {
  margin: 0;
  color: var(--color-primary);
  font-size: 22px;
  font-weight: 600;
  line-height: 1.35;
}

.newsflash-title-link {
  text-decoration: none;
}

.newsflash-item-link:hover .newsflash-title-link {
  text-decoration: underline;
}

.newsflash-card-footer {
  display: flex;
  justify-content: flex-end;
}

.newsflash-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-highlight);
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.newsflash-cta .material-icons-outlined {
  font-size: 18px;
}

.read-more-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-highlight);
  color: var(--color-text-on-highlight);
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--highlight-hover);
    color: var(--color-text-on-highlight);
  }
}

.detail-card {
  background-color: var(--color-surface);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 16px rgba(15, 15, 26, 0.05);
}

.detail-topline {
  display: flex;
  align-items: center;
}

.detail-topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.detail-block {
  padding: 20px;
  margin-bottom: 16px;
  border-radius: 12px;
  background-color: var(--color-background);
  border: 1px solid var(--color-outline);

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-title {
  margin: 0 0 12px 0;
  color: var(--color-primary);
  font-size: 20px;
  font-weight: 600;
}

.detail-text {
  margin: 0;
  white-space: pre-line;
}

.stocks-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stock-item {
  padding: 16px;
  border-radius: 12px;
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
}

.stock-name {
  color: var(--color-text-primary);
  font-weight: 600;
}

.stock-detail-group + .stock-detail-group {
  margin-top: 12px;
}

.stock-detail-label {
  margin: 12px 0 6px 0;
  color: var(--color-text-secondary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.stock-reason {
  margin: 8px 0 0 0;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .informe-main-container {
    padding: 24px 16px 32px;
  }
}

@media (max-width: 640px) {
  .detail-card,
  .content-state {
    padding: 20px;
  }

  .newsflash-item-shell {
    padding: 18px;
  }

  .informe-title {
    font-size: 20px;
  }

  .detail-topbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-block {
    padding: 16px;
  }
}
</style>
